import type { Email, Folder, EmailAccount, Label, Attachment, SendEmailRequest, EmailApiResponse, QueuedEmail, OutboxItem, QueuedSendResponse, CustomFolderRequest } from '../types';
import { authService } from '@/lib/authService';

// Use local Next.js API routes which proxy to the websocket server
const API_BASE = '';

class EmailApiService {
  private getAuthHeaders(): Record<string, string> {
    const tokens = authService.getTokens();
    const token = tokens?.access_token || null;
    
    // Debug logging
    console.log('[EmailAPI] getAuthHeaders - tokens:', tokens ? 'present' : 'null', 'access_token:', token ? `${token.substring(0, 20)}...` : 'null');
    
    if (!token || token.trim() === '') {
      console.warn('[EmailAPI] No valid access token available');
      return {
        'Content-Type': 'application/json',
      };
    }
    
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<EmailApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      const text = await response.text();
      let data: any = {};

      if (text) {
        try {
          data = JSON.parse(text);
        } catch (parseError: any) {
          console.error('[EmailAPI] Failed to parse JSON response:', {
            endpoint,
            status: response.status,
            statusText: response.statusText,
            error: parseError?.message,
            responsePreview: text.slice(0, 500),
          });

          return {
            success: false,
            error: response.ok
              ? 'Received an invalid response from the email service'
              : `Email service returned an invalid response (${response.status})`,
          };
        }
      }

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || `Request failed: ${response.status}`,
        };
      }

      // Handle wrapped response from proxy
      if (data.success !== undefined) {
        return data;
      }

      return { success: true, data };
    } catch (error: any) {
      console.error('Email API error:', error);
      return {
        success: false,
        error: error.message || 'Network error',
      };
    }
  }

  // Account Management
  async getAccounts(): Promise<EmailApiResponse<EmailAccount[]>> {
    return this.request<EmailAccount[]>('/api/v1/email/accounts');
  }

  async addEmailAccount(account: {
    email: string;
    password: string;
    name?: string;
    provider?: string;
    imap: { host: string; port: number; secure: boolean };
    smtp: { host: string; port: number; secure: boolean };
  }): Promise<EmailApiResponse<EmailAccount>> {
    return this.request<EmailAccount>('/api/v1/email/accounts', {
      method: 'POST',
      body: JSON.stringify(account),
    });
  }

  async testAccountSettings(settings: {
    email: string;
    password: string;
    imap: { host: string; port: number; secure: boolean };
    smtp: { host: string; port: number; secure: boolean };
  }): Promise<EmailApiResponse<{ valid: boolean }>> {
    return this.request('/api/v1/email/test', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  async removeAccount(accountId: string): Promise<EmailApiResponse<void>> {
    return this.request(`/api/v1/email/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get domain alias mapping for display purposes
   * Maps totalhub.online addresses to their custom domain equivalents
   */
  async getDomainAliasMapping(): Promise<{
    success: boolean;
    error?: string;
    hasCustomDomain?: boolean;
    emailMapping?: Record<string, string>;   // primary_email -> custom_domain_email
    domainMapping?: Record<string, string>;  // primary_domain -> custom_domain
  }> {
    return this.request('/api/v1/email/domain-alias-mapping') as any;
  }

  // Email Operations
  async getEmails(
    folder: string = 'INBOX',
    options?: { page?: number; limit?: number; sharedMailboxId?: number | null }
  ): Promise<EmailApiResponse<Email[]>> {
    const params = new URLSearchParams();
    params.set('folder', folder);
    if (options?.page) params.set('page', String(options.page));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.sharedMailboxId) params.set('shared_mailbox_id', String(options.sharedMailboxId));

    return this.request<Email[]>(`/api/v1/email/messages?${params.toString()}`);
  }

  async getEmail(
    messageId: string,
    mailboxId?: number,
  ): Promise<EmailApiResponse<Email & { thread_id?: string; account_id?: number; client_id?: number; message_id?: string }>> {
    const params = new URLSearchParams();
    if (mailboxId) params.set('mailboxId', String(mailboxId));
    const query = params.toString();
    return this.request<Email & { thread_id?: string; account_id?: number; client_id?: number; message_id?: string }>(
      `/api/v1/email/messages/${encodeURIComponent(messageId)}${query ? `?${query}` : ''}`
    );
  }

  /**
   * Search emails with full-text search and advanced filters
   * Uses MongoDB text index for fast, relevance-ranked results
   */
  async searchEmails(query: string, options?: {
    folder?: string;
    dateFrom?: string;
    dateTo?: string;
    hasAttachment?: boolean;
    from?: string;
    to?: string;
    isUnread?: boolean;
    isStarred?: boolean;
    limit?: number;
    page?: number;
  }): Promise<EmailApiResponse<Email[]> & { pagination?: { total: number; page: number; limit: number; pages: number } }> {
    const params = new URLSearchParams({ query });
    
    if (options?.folder) params.append('folder', options.folder);
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options?.dateTo) params.append('dateTo', options.dateTo);
    if (options?.hasAttachment !== undefined) params.append('hasAttachment', String(options.hasAttachment));
    if (options?.from) params.append('from', options.from);
    if (options?.to) params.append('to', options.to);
    if (options?.isUnread !== undefined) params.append('isUnread', String(options.isUnread));
    if (options?.isStarred !== undefined) params.append('isStarred', String(options.isStarred));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.page) params.append('page', String(options.page));
    
    return this.request<Email[]>(`/api/v1/email/search?${params.toString()}`);
  }

  async markEmailAsRead(
    messageId: string,
    read: boolean = true
  ): Promise<EmailApiResponse<void>> {
    return this.request(`/api/v1/email/messages/${encodeURIComponent(messageId)}/read`, {
      method: 'PUT',
      body: JSON.stringify({ read }),
    });
  }

  /**
   * Mark thread as read/unread with real-time sync across all devices
   * This triggers an instant WebSocket broadcast to all connected sessions
   * @param threadId - ID of the thread to mark
   * @param is_read - Read status
   * @param account_id - Optional mailbox ID for shared mailboxes
   */
  async markThreadAsReadSync(
    threadId: string,
    is_read: boolean = true,
    account_id?: number
  ): Promise<EmailApiResponse<{ updated: number; thread_id: string; is_read: boolean }>> {
    return this.request('/api/v1/email-threads/mark-read-sync', {
      method: 'PUT',
      body: JSON.stringify({ 
        threadId,
        is_read,
        account_id
      }),
    });
  }

  async toggleEmailStar(
    messageId: string,
    starred: boolean
  ): Promise<EmailApiResponse<void>> {
    return this.request(`/api/v1/email/messages/${encodeURIComponent(messageId)}/star`, {
      method: 'PUT',
      body: JSON.stringify({ starred }),
    });
  }

  async deleteEmail(messageId: string): Promise<EmailApiResponse<void>> {
    return this.request(`/api/v1/email/messages/${encodeURIComponent(messageId)}`, {
      method: 'DELETE',
    });
  }

  async emptyTrash(): Promise<EmailApiResponse<{ deleted: number; message: string }>> {
    return this.request('/api/v1/email/trash/empty', {
      method: 'DELETE',
    });
  }

  async moveEmail(messageId: string, toFolder: string, sharedMailboxId?: number): Promise<EmailApiResponse<void>> {
    return this.request(`/api/v1/email/messages/${encodeURIComponent(messageId)}/move`, {
      method: 'PUT',
      body: JSON.stringify({ 
        folder: toFolder,
        shared_mailbox_id: sharedMailboxId
      }),
    });
  }

  /**
   * Send email - uses queue-based sending by default for instant response
   * The email is queued in MongoDB and sent asynchronously, providing
   * Exchange-like instant send experience
   */
  async sendEmail(email: SendEmailRequest): Promise<EmailApiResponse<{ messageId: string; queued?: boolean; queueId?: string }>> {
    // Default to queue-based sending for instant response
    const useQueue = email.useQueue !== false;
    
    if (useQueue) {
      return this.queueEmail(email);
    }
    
    // Synchronous send (slower, waits for SMTP)
    return this.request('/api/v1/email/send', {
      method: 'POST',
      body: JSON.stringify(email),
    });
  }

  /**
   * Save draft to server (IMAP Drafts folder)
   * Returns the draft's messageId for tracking
   */
  async saveDraft(draft: {
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    body?: string;
    accountId?: number;
    draftId?: string; // Existing draft ID to update
  }): Promise<EmailApiResponse<{ messageId: string; folder: string; uid?: number }>> {
    return this.request('/api/v1/email/draft', {
      method: 'POST',
      body: JSON.stringify(draft),
    });
  }

  /**
   * Queue email for background sending (Exchange-like instant send)
   * Returns immediately, email is sent asynchronously via the queue processor
   */
  async queueEmail(email: SendEmailRequest): Promise<EmailApiResponse<{ messageId: string; queued: boolean; queueId?: string }>> {
    return this.request('/api/v1/email/queue/send', {
      method: 'POST',
      body: JSON.stringify({
        ...email,
        priority: email.priority || 5,
      }),
    });
  }

  /**
   * Cancel a queued email before it's sent
   */
  async cancelQueuedEmail(queueId: string): Promise<EmailApiResponse<{ cancelled: boolean }>> {
    return this.request(`/api/v1/email/queue/${encodeURIComponent(queueId)}/cancel`, {
      method: 'POST',
    });
  }

  /**
   * Retry a failed queued email
   */
  async retryQueuedEmail(queueId: string): Promise<EmailApiResponse<{ retrying: boolean }>> {
    return this.request(`/api/v1/email/queue/${encodeURIComponent(queueId)}/retry`, {
      method: 'POST',
    });
  }

  // ============================================================================
  // OUTBOX / EMAIL QUEUE OPERATIONS
  // ============================================================================

  /**
   * Get outbox emails (queued, sending, failed emails)
   */
  async getOutbox(options?: {
    status?: 'queued' | 'sending' | 'failed' | 'all';
    limit?: number;
    skip?: number;
    sharedMailboxId?: number | null;
  }): Promise<EmailApiResponse<{
    items: OutboxItem[];
    stats: {
      queued: number;
      sending: number;
      failed: number;
      total: number;
    };
  }>> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.skip) params.set('skip', String(options.skip));
    // Pass shared mailbox filter - null means personal mailbox only, undefined means all
    if (options?.sharedMailboxId !== undefined) {
      params.set('shared_mailbox_id', options.sharedMailboxId === null ? 'null' : String(options.sharedMailboxId));
    }
    
    return this.request(`/api/v1/email/outbox?${params.toString()}`);
  }

  /**
   * Get single outbox item details
   */
  async getOutboxItem(queueId: string): Promise<EmailApiResponse<OutboxItem>> {
    return this.request(`/api/v1/email/outbox/${encodeURIComponent(queueId)}`);
  }

  /**
   * Cancel/delete an outbox item (queued, failed, or scheduled)
   */
  async cancelOutboxItem(queueId: string): Promise<EmailApiResponse<{ message: string }>> {
    return this.request(`/api/v1/email/outbox/${encodeURIComponent(queueId)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Retry a failed outbox item
   */
  async retryOutboxItem(queueId: string): Promise<EmailApiResponse<{ message: string }>> {
    return this.request(`/api/v1/email/outbox/${encodeURIComponent(queueId)}/retry`, {
      method: 'POST',
    });
  }

  /**
   * Trigger immediate processing of due queued/scheduled outbox emails.
   */
  async processOutbox(limit = 20): Promise<EmailApiResponse<{
    processed: number;
    successful: number;
    failed: number;
    errors?: Array<{ id: string; error: string }>;
    message?: string;
  }>> {
    return this.request('/api/v1/email/outbox/process', {
      method: 'POST',
      body: JSON.stringify({ limit }),
    });
  }

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<EmailApiResponse<{
    pending: number;
    sending: number;
    sent: number;
    failed: number;
    totalToday: number;
    avgDeliveryTime: number; // in seconds
  }>> {
    return this.request('/api/v1/email/queue/stats');
  }

  // Folder Operations
  async getFolders(mailboxId?: number): Promise<EmailApiResponse<Folder[]>> {
    const url = mailboxId
      ? `/api/v1/email/folders?mailboxId=${mailboxId}`
      : '/api/v1/email/folders';
    return this.request<Folder[]>(url);
  }

  /**
   * Create a new folder (supports custom folders and shared mailbox folders)
   * @param name - Folder name
   * @param parentPath - Optional parent folder path
   * @param options - Optional color, icon, and mailboxId for shared mailboxes
   */
  async createFolder(
    name: string,
    parentPath?: string,
    options?: { color?: string; icon?: string; mailboxId?: number }
  ): Promise<EmailApiResponse<Folder>> {
    return this.request<Folder>('/api/v1/email/folders', {
      method: 'POST',
      body: JSON.stringify({ name, parentPath, ...options }),
    });
  }

  /**
   * Update a custom folder (rename, change color/icon)
   */
  async updateFolder(
    folderPath: string,
    updates: { name?: string; color?: string; icon?: string; mailboxId?: number }
  ): Promise<EmailApiResponse<Folder>> {
    return this.request<Folder>(`/api/v1/email/folders/${encodeURIComponent(folderPath)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteFolder(folderPath: string, mailboxId?: number): Promise<EmailApiResponse<void>> {
    const url = mailboxId
      ? `/api/v1/email/folders/${encodeURIComponent(folderPath)}?mailboxId=${mailboxId}`
      : `/api/v1/email/folders/${encodeURIComponent(folderPath)}`;
    return this.request(url, {
      method: 'DELETE',
    });
  }

  /**
   * Get custom folders only
   */
  async getCustomFolders(): Promise<EmailApiResponse<Folder[]>> {
    return this.request<Folder[]>('/api/v1/email/folders/custom');
  }

  async syncFolder(folder: string): Promise<EmailApiResponse<{ syncedCount: number }>> {
    return this.request(`/api/v1/email/folders/${encodeURIComponent(folder)}/sync`, {
      method: 'POST',
    });
  }

  // Labels
  async getLabels(): Promise<EmailApiResponse<Label[]>> {
    return this.request<Label[]>('/api/v1/email/labels');
  }

  async createLabel(name: string, color?: string): Promise<EmailApiResponse<Label>> {
    return this.request<Label>('/api/v1/email/labels', {
      method: 'POST',
      body: JSON.stringify({ name, color }),
    });
  }

  async addLabelToEmail(
    messageId: string,
    label: string
  ): Promise<EmailApiResponse<void>> {
    return this.request(`/api/v1/email/messages/${encodeURIComponent(messageId)}/labels`, {
      method: 'POST',
      body: JSON.stringify({ label }),
    });
  }

  async removeLabelFromEmail(
    messageId: string,
    label: string
  ): Promise<EmailApiResponse<void>> {
    return this.request(
      `/api/v1/email/messages/${encodeURIComponent(messageId)}/labels/${encodeURIComponent(label)}`,
      {
        method: 'DELETE',
      }
    );
  }

  // Attachments
  async downloadAttachment(messageId: string, attachmentId: string): Promise<Blob> {
    const response = await fetch(
      `${API_BASE}/api/v1/email/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download attachment');
    }

    return response.blob();
  }

  // ============================================================================
  // EMAIL TO WORK ITEM CONVERSION
  // ============================================================================

  /**
   * Check if an email has been converted to a work item
   */
  async checkEmailWorkItem(messageId: string): Promise<EmailApiResponse<{
    hasWorkItem: boolean;
    workItem: {
      id: string;
      item_number: string;
      item_type: string;
      title: string;
      status: string;
      priority: string;
      assigned_to: number | null;
      assignee: { id: number; name: string; email: string } | null;
      due_date: string | null;
      created_at: string;
    } | null;
  }>> {
    return this.request(`/api/v1/email/messages/${encodeURIComponent(messageId)}/work-item`);
  }

  /**
   * Batch check which emails have been converted to work items
   */
  async batchCheckEmailWorkItems(messageIds: string[]): Promise<{
    success: boolean;
    error?: string;
    workItems?: Record<string, {
      hasWorkItem: boolean;
      workItem: {
        id: string;
        item_number: string;
        item_type: string;
        title: string;
        status: string;
        priority: string;
        assigned_to: number | null;
        assignee: { id: number; name: string; email: string } | null;
        due_date: string | null;
        created_at: string;
      } | null;
    }>;
  }> {
    const normalizedMessageIds = Array.from(
      new Set(
        messageIds
          .map((messageId) => (typeof messageId === 'string' ? messageId.trim() : ''))
          .filter(Boolean)
      )
    );

    if (normalizedMessageIds.length === 0) {
      return {
        success: true,
        workItems: {},
      };
    }

    const maxBatchSize = 100;
    const batches: string[][] = [];

    for (let index = 0; index < normalizedMessageIds.length; index += maxBatchSize) {
      batches.push(normalizedMessageIds.slice(index, index + maxBatchSize));
    }

    const mergedWorkItems: Record<string, {
      hasWorkItem: boolean;
      workItem: {
        id: string;
        item_number: string;
        item_type: string;
        title: string;
        status: string;
        priority: string;
        assigned_to: number | null;
        assignee: { id: number; name: string; email: string } | null;
        due_date: string | null;
        created_at: string;
      } | null;
    }> = {};

    for (const batch of batches) {
      const batchResult = await this.request('/api/v1/email/work-items/batch', {
        method: 'POST',
        body: JSON.stringify({ messageIds: batch }),
      }) as any;

      if (!batchResult.success) {
        return batchResult;
      }

      Object.assign(mergedWorkItems, batchResult.workItems || {});
    }

    return {
      success: true,
      workItems: mergedWorkItems,
    };
  }

  /**
   * Create a work item from an email
   */
  async createTaskFromEmail(params: {
    messageId: string;
    itemType?: string;
    title?: string;
    description?: string;
    assigneeId?: number;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    dueDate?: string;
    contactId?: number;
    contactPersonId?: number;
    locationId?: number;
    contactName?: string;
    contactPersonName?: string;
    locationName?: string;
    tags?: string[];
  }): Promise<EmailApiResponse<{
    workItem: {
      id: string;
      item_number: string;
      item_type: string;
      title: string;
      status: string;
      priority: string;
      assigned_to: number | null;
      assignee: { id: number; name: string; email: string } | null;
      due_date: string | null;
      contact_id: number | null;
      created_at: string;
    };
    message: string;
  }>> {
    return this.request('/api/v1/email/create-task', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /**
   * Quick assign an email to an employee (creates task immediately)
   */
  async assignEmail(params: {
    messageId: string;
    assigneeId: number;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    dueDate?: string;
    note?: string;
  }): Promise<EmailApiResponse<{
    created?: boolean;
    updated?: boolean;
    workItem: {
      id: string;
      item_number: string;
      title: string;
      assignee: { id: number; name: string; email: string };
    };
    message: string;
  }>> {
    return this.request('/api/v1/email/assign', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /**
   * Get available users for email assignment
   * Returns { success: true, assignees: [...] } directly (not wrapped in data)
   */
  async getAssignees(search?: string): Promise<{ 
    success: boolean; 
    error?: string;
    assignees?: Array<{
      id: number;
      email: string;
      name: string;
      firstName: string | null;
      lastName: string | null;
      avatar: string | null;
    }>;
  }> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/api/v1/email/assignees${params}`) as any;
  }

  /**
   * Get email content for a work item
   */
  async getWorkItemEmail(workItemId: string): Promise<EmailApiResponse<{
    emailCached: boolean;
    email?: {
      messageId: string;
      from: { address: string; name?: string };
      to: Array<{ address: string; name?: string }>;
      cc?: Array<{ address: string; name?: string }>;
      subject: string;
      date: string;
      html: string;
      text: string;
      attachments: Array<{
        id: string;
        filename: string;
        contentType: string;
        size: number;
      }>;
    };
    metadata: {
      email_from: { address: string; name?: string };
      email_to: Array<{ address: string; name?: string }>;
      email_subject: string;
      email_date: string;
      has_attachments: boolean;
      attachment_count: number;
    };
  }>> {
    return this.request(`/api/v1/email/work-items/${encodeURIComponent(workItemId)}/email`);
  }

  // ============================================================================
  // DELEGATED / SHARED MAILBOX METHODS
  // ============================================================================

  /**
   * Get delegated/shared mailbox accounts the user has access to
   */
  async getDelegatedAccounts(): Promise<EmailApiResponse<{
    delegatedAccounts: Array<{
      accessId: number;
      mailboxId: number;
      emailAddress: string;
      displayName: string;
      domainName?: string;
      permissions: {
        canRead: boolean;
        canSend: boolean;
        canSendAs: boolean;
        canSendOnBehalf: boolean;
        canManage: boolean;
      };
      isShared: boolean;
    }>;
    ownAccounts: Array<{
      mailboxId: number;
      emailAddress: string;
      displayName: string;
      isShared: boolean;
    }>;
  }>> {
    return this.request('/api/v1/email/delegated-accounts');
  }

  /**
   * Get all delegates (users with access) for a shared mailbox
   */
  async getMailboxDelegates(mailboxId: number): Promise<EmailApiResponse<{
    delegates: Array<{
      userId: number;
      name: string;
      firstName: string;
      lastName: string;
      email: string;
      avatarUrl: string | null;
      permissions: {
        canRead: boolean;
        canSend: boolean;
        canManage: boolean;
      };
    }>;
  }>> {
    return this.request(`/api/v1/email/mailbox/${mailboxId}/delegates`);
  }

  /**
   * Fetch emails from a shared/delegated mailbox
   */
  async fetchSharedMailboxEmails(params: {
    mailboxId: number;
    folder?: string;
    limit?: number;
  }): Promise<EmailApiResponse<{
    data: Email[];
    mailbox: {
      id: number;
      email: string;
      displayName: string;
    };
  }>> {
    return this.request('/api/v1/email/fetch-shared', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /**
   * Get cached emails from a shared mailbox
   */
  async getSharedMailboxEmails(
    mailboxId: number,
    options?: {
      folder?: string;
      skip?: number;
      limit?: number;
      unreadOnly?: boolean;
    }
  ): Promise<EmailApiResponse<{
    data: Email[];
    pagination: {
      skip: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  }>> {
    const params = new URLSearchParams();
    if (options?.folder) params.append('folder', options.folder);
    if (options?.skip) params.append('skip', String(options.skip));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.unreadOnly) params.append('unreadOnly', 'true');
    
    return this.request(`/api/v1/email/shared/${mailboxId}?${params}`);
  }

  /**
   * Mark a shared mailbox email as read/unread for the current user
   */
  async markSharedEmailRead(
    mailboxId: number,
    messageId: string,
    isRead: boolean = true
  ): Promise<EmailApiResponse<{ message: string; modified: number }>> {
    return this.request(`/api/v1/email/shared/${mailboxId}/mark-read`, {
      method: 'POST',
      body: JSON.stringify({ messageId, isRead }),
    });
  }

  /**
   * Get email tasks (work items created from emails)
   */
  async getEmailTasks(params?: {
    status?: string;
    priority?: string;
    assigneeId?: number;
    mailboxId?: number;
    skip?: number;
    limit?: number;
    search?: string;
  }): Promise<EmailApiResponse<{
    data: Array<{
      id: string;
      item_number: string;
      title: string;
      status: string;
      priority: string;
      created_at: string;
      due_date?: string;
      email_from?: string;
      email_subject?: string;
      source_mailbox?: string;
      assignees: Array<{
        user_id: number;
        role: string;
        first_name: string;
        last_name: string;
        email: string;
      }>;
    }>;
    pagination: {
      skip: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  }>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.assigneeId) searchParams.append('assigneeId', String(params.assigneeId));
    if (params?.mailboxId) searchParams.append('mailboxId', String(params.mailboxId));
    if (params?.skip) searchParams.append('skip', String(params.skip));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.search) searchParams.append('search', params.search);
    
    return this.request(`/api/v1/email/email-tasks?${searchParams}`);
  }

  /**
   * Get email task statistics
   */
  async getEmailTaskStats(mailboxId?: number): Promise<EmailApiResponse<{
    stats: {
      byStatus: Record<string, number>;
      byPriority: Record<string, number>;
      byMailbox: Array<{
        accountId: number;
        email: string;
        displayName: string;
        count: number;
      }>;
      overdue: number;
      unassigned: number;
      total: number;
    };
  }>> {
    const params = mailboxId ? `?mailboxId=${mailboxId}` : '';
    return this.request(`/api/v1/email/email-tasks/stats${params}`);
  }

  // ============================================================================
  // EMAIL THREADS API (MongoDB-based unified messaging)
  // ============================================================================

  /**
   * Get email threads list from MongoDB
   * Threads are email conversations grouped by subject and participants
   */
  async getThreads(options?: {
    folder?: string;
    limit?: number;
    skip?: number;
    unread?: boolean;
    starred?: boolean;
    search?: string;
    participant?: string;
    accountId?: number; // For shared mailbox filtering
    includeSharedMailboxes?: boolean;
  }): Promise<EmailApiResponse<{
    threads: Array<{
      thread_id: string;
      subject: string;
      participants: Array<{ name?: string; email: string }>;
      last_message_at: string;
      last_message_preview?: string;
      last_message_from?: { name?: string; email: string };
      folder: string;
      labels?: string[];
      has_unread: boolean;
      unread_count: number;
      starred: boolean;
      message_count: number;
      messages?: Array<any>; // Last message for preview
    }>;
    count: number;
  }>> {
    const params = new URLSearchParams();
    if (options?.folder) params.set('folder', options.folder);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.skip) params.set('skip', String(options.skip));
    if (options?.unread) params.set('unread', 'true');
    if (options?.starred) params.set('starred', 'true');
    if (options?.search) params.set('search', options.search);
    if (options?.participant) params.set('participant', options.participant);
    if (options?.accountId) params.set('account_id', String(options.accountId));
    if (options?.includeSharedMailboxes) params.set('include_shared', 'true');
    
    return this.request(`/api/v1/email-threads?${params.toString()}`);
  }

  /**
   * Get a single thread with all messages
   * Use this when user clicks on a thread to see full conversation
   * @param threadId - ID of the thread to fetch
   * @param accountId - Optional mailbox ID for shared mailbox threads
   */
  async getThread(threadId: string, accountId?: number): Promise<EmailApiResponse<{
    thread: {
      thread_id: string;
      subject: string;
      participants: Array<{ name?: string; email: string }>;
      folder: string;
      labels?: string[];
      has_unread: boolean;
      unread_count: number;
      starred: boolean;
      message_count: number;
      messages: Array<{
        message_id: string;
        from: { name?: string; email: string };
        to: Array<{ name?: string; email: string }>;
        cc?: Array<{ name?: string; email: string }>;
        subject?: string;
        text_content?: string;
        html_content?: string;
        snippet?: string;
        date: string;
        is_read: boolean;
        is_starred?: boolean;
        attachments?: Array<any>;
        source_type?: 'imap' | 'internal' | 'sent';
        security_analysis?: any; // Email security analysis data
      }>;
      last_message_at: string;
      created_at?: string;
      updated_at?: string;
      read_by_users?: number[]; // For shared mailbox read tracking
      account_id?: number; // Mailbox ID for shared mailbox threads
    };
  }>> {
    const url = accountId 
      ? `/api/v1/email-threads/${encodeURIComponent(threadId)}?account_id=${accountId}`
      : `/api/v1/email-threads/${encodeURIComponent(threadId)}`;
    return this.request(url);
  }

  /**
   * Mark a thread as read or unread
   * Updates all messages in the thread
   */
  async markThreadAsRead(threadId: string, read: boolean = true): Promise<EmailApiResponse<{ success: boolean }>> {
    return this.request(`/api/v1/email-threads/${encodeURIComponent(threadId)}/read`, {
      method: 'POST',
      body: JSON.stringify({ read }),
    });
  }

  /**
   * Toggle thread starred status
   */
  async toggleThreadStarred(threadId: string, starred: boolean): Promise<EmailApiResponse<{ success: boolean }>> {
    return this.request(`/api/v1/email-threads/${encodeURIComponent(threadId)}/star`, {
      method: 'POST',
      body: JSON.stringify({ starred }),
    });
  }

  /**
   * Delete a thread
   * Moves to Trash, or permanently deletes if already in Trash
   */
  async deleteThread(threadId: string, permanent: boolean = false): Promise<EmailApiResponse<{
    deleted: number;
    permanent: boolean;
    moved_to?: string;
  }>> {
    return this.request(`/api/v1/email-threads/${encodeURIComponent(threadId)}${permanent ? '?permanent=true' : ''}`, {
      method: 'DELETE',
    });
  }

  /**
   * Move a thread to a different folder
   * @param threadId - Thread ID
   * @param folder - Target folder (Trash, Archive, INBOX, etc.)
   */
  async moveThread(threadId: string, folder: string): Promise<EmailApiResponse<{
    moved: number;
    from_folder: string;
    to_folder: string;
  }>> {
    return this.request(`/api/v1/email-threads/${encodeURIComponent(threadId)}/move`, {
      method: 'PUT',
      body: JSON.stringify({ folder }),
    });
  }

  /**
   * Get thread statistics
   * Returns counts of total threads, messages, unread, starred
   */
  async getThreadStats(options?: {
    accountId?: number;
    includeSharedMailboxes?: boolean;
  }): Promise<EmailApiResponse<{
    stats: {
      total_threads: number;
      total_messages: number;
      unread_threads: number;
      starred_threads: number;
      folders?: Record<string, number>;
    };
  }>> {
    const params = new URLSearchParams();
    if (options?.accountId) params.set('account_id', String(options.accountId));
    if (options?.includeSharedMailboxes) params.set('include_shared', 'true');

    const queryString = params.toString();
    return this.request(`/api/v1/email-threads/stats${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Trigger backfill of existing email_cache data into threads
   * Admin/utility function - use sparingly
   */
  async backfillThreads(options?: {
    accountId?: number;
    limit?: number;
  }): Promise<EmailApiResponse<{
    success: boolean;
    processed: number;
    threadsCreated: number;
  }>> {
    return this.request('/api/v1/email-threads/backfill', {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  }
}

const emailApiService = new EmailApiService();
export default emailApiService;
